import hasLinkInBioLab from '@dashboard/lib/linkInBioLab';
import { isHalloweenOn, isHalloweenSeason } from '@dashboard/lib/theme';

export const DISMISS_STORAGE_KEY = 'trick-or-treat-dismissed';

export type TreatKey = 'orderBumps' | 'linkInBio' | 'halloweenTheme';
export type TrickKey = 'haunted' | 'ghost' | 'bats';
export type OutcomeKey = TreatKey | TrickKey;

export type Outcome = {
  key: OutcomeKey;
  kind: 'treat' | 'trick';
  title: string;
  body: string;
  action?: string;
};

export const OUTCOMES: Record<OutcomeKey, Outcome> = {
  orderBumps: {
    key: 'orderBumps',
    kind: 'treat',
    title: 'Treat! Your customers to order bumps.',
    body: "Give your customers a frightfully pleasant surprise. Give them an offer they can't refuse.",
    action: 'Show me',
  },
  linkInBio: {
    key: 'linkInBio',
    kind: 'treat',
    title: 'Treat! One link.',
    body: 'Replace your old bio links with an entire candy bowl. Your checkout pages, events, forms and more! Visitors can take all they want.',
    action: 'Show me',
  },
  halloweenTheme: {
    key: 'halloweenTheme',
    kind: 'treat',
    title: 'Treat! Dress up your dashboard.',
    body: 'Pumpkin orange, midnight purple and zero itchy fabric. It takes the costume off by itself after Halloween.',
    action: 'Dress it up',
  },
  haunted: {
    key: 'haunted',
    kind: 'trick',
    title: 'Trick! Who turned off the lights?',
    body: 'Your dashboard is having a spooky moment. The lights come back on in a few seconds.',
  },
  ghost: {
    key: 'ghost',
    kind: 'trick',
    title: 'Trick! A ghost has borrowed a word.',
    body: "It's a very polite ghost. Watch, it'll bring it right back.",
  },
  bats: {
    key: 'bats',
    kind: 'trick',
    title: 'Trick! Bats in the dashboard!',
    body: "Don't worry, they're just passing through on their way to a party.",
  },
};

type Seller = Parameters<typeof hasLinkInBioLab>[0];

export function availableOutcomes(seller: Seller): OutcomeKey[] {
  const treats: TreatKey[] = ['orderBumps'];
  if (hasLinkInBioLab(seller)) treats.push('linkInBio');
  if (!isHalloweenOn()) treats.push('halloweenTheme');
  return [...treats, 'haunted', 'ghost', 'bats'];
}

/** Treat or trick is a coin toss first, so a seller with fewer treats still gets half treats. */
export function pickOutcome(
  seller: Seller,
  previous: OutcomeKey | null,
  random: () => number = Math.random,
): Outcome {
  const keys = availableOutcomes(seller).filter(key => key !== previous);
  const kind = random() < 0.5 ? 'treat' : 'trick';
  const pool = keys.filter(key => OUTCOMES[key].kind === kind);
  const choices = pool.length ? pool : keys;
  return OUTCOMES[choices[Math.floor(random() * choices.length)]];
}

export function isTrickOrTreatShown(now = new Date()) {
  if (!isHalloweenSeason(now)) return false;
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) !== String(now.getFullYear());
  } catch {
    return true;
  }
}

export function dismissTrickOrTreat() {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(new Date().getFullYear()));
  } catch {
    // Storage blocked: hidden until the page reloads.
  }
}
